import "./RequestNearbyDriversModal.css";
import React, { useEffect, useMemo, useState } from "react";
import { geocodeAddress, haversineKm, formatKm } from "../utils/geo";
import {
  watchDriverOffersByMeeting,
  takeSeatForRequest,
  deleteDriverOffer,
} from "../services/firebaseApi";
import { database } from "../firebase";
import { ref, onValue } from "firebase/database";
import { normalizeMeetingTimestamp, toIsoDate } from "../utils/dateHelpers";

const RequestNearbyDriversModal = ({ request, onClose, onGoToDriverForm }) => {
  const [offers, setOffers] = useState([]);
  const [requestGeo, setRequestGeo] = useState(request.geo || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  // Cache local des géocoordonnées des offres (par id)
  const [offerGeo, setOfferGeo] = useState({});
  // État local pour la demande mise à jour en temps réel
  const [currentRequest, setCurrentRequest] = useState(request);

  // Écouter les changements de la demande en temps réel
  useEffect(() => {
    if (!request?.id) return;
    
    const requestRef = ref(database, `requests/${request.id}`);
    const unsubscribe = onValue(requestRef, (snapshot) => {
      if (snapshot.exists()) {
        const updatedRequest = { id: request.id, ...snapshot.val() };
        console.log('🔄 [RequestNearbyDriversModal] Demande mise à jour:', {
          id: updatedRequest.id,
          name: updatedRequest.name || updatedRequest.passengerName,
          assigned: updatedRequest.assigned,
          seatsAssigned: updatedRequest.seatsAssigned,
          seatsRemaining: updatedRequest.seatsRemaining,
          drivers: updatedRequest.drivers
        });
        setCurrentRequest(updatedRequest);
      }
    }, (error) => {
      console.error('❌ [RequestNearbyDriversModal] Erreur listener demande:', error);
    });
    
    return () => {
      try { unsubscribe(); } catch {}
    };
  }, [request.id]);

  useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        const req = currentRequest || request;
        let geo = req.geo;
        
        // Utiliser les coordonnées GPS si disponibles (pour les parkings)
        if (!geo && req.latitude && req.longitude) {
          geo = {
            lat: parseFloat(req.latitude),
            lon: parseFloat(req.longitude)
          };
          console.log('✅ Utilisation coordonnées GPS de la demande:', geo);
          setRequestGeo(geo);
        } else if (!geo) {
          // Utiliser l'adresse réelle si disponible (pour les parkings)
          const addressToGeocode = req.realAddress || req.address;
          const addressString = `${addressToGeocode}, ${req.postalCode || ""} ${req.city || ""}`.trim();
          
          try {
            // Ajouter un timeout pour éviter les blocages
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Timeout géocodage')), 3000)
            );
            
            const geoPromise = geocodeAddress(addressString);
            geo = await Promise.race([geoPromise, timeoutPromise]);
            setRequestGeo(geo);
            console.log('✅ Géocodage réussi pour la demande:', geo);
          } catch (geoError) {
            console.warn('⚠️ Erreur géocodage demande (non bloquant):', geoError.message);
            setRequestGeo(null); // Continuer sans géolocalisation
          }
        } else {
          setRequestGeo(geo);
        }
        
        // Normaliser le meetingAt pour corriger les problèmes de fuseau horaire
        const normalizedMeetingAt = normalizeMeetingTimestamp(req);
        const normalizedIsoDate = req.isoDate || (normalizedMeetingAt ? toIsoDate(normalizedMeetingAt) : null);
        
        console.log('🔍 [RequestNearbyDriversModal] Recherche offres avec:', {
          originalMeetingAt: req.meetingAt,
          normalizedMeetingAt,
          originalIsoDate: req.isoDate,
          normalizedIsoDate,
          name: req.name || req.passengerName
        });
        
        unsub = watchDriverOffersByMeeting({
          meetingAt: normalizedMeetingAt,
          isoDate: normalizedIsoDate,
          onData: setOffers,
          onError: (e) => console.error("Nearby drivers watcher error:", e),
        });
      } catch (e) {
        // Ne pas bloquer si l'adresse n'est pas trouvée - on peut quand même prendre la personne
        console.warn("⚠️ Adresse introuvable pour géocodage, mais on peut quand même prendre la personne:", e.message);
        setError(null); // Ne pas afficher d'erreur bloquante
        setRequestGeo(null); // Pas de géolocalisation mais on continue
      } finally {
        setLoading(false);
      }
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, [currentRequest, request]);

  // Géocoder les offres qui n'ont pas encore de coordonnées (en parallèle)
  useEffect(() => {
    if (!offers || offers.length === 0) return;
    
    (async () => {
      // Filtrer les offres qui ont besoin de géocodage
      const offersToGeocode = offers.filter(o => 
        o && !o.geo && !offerGeo[o.id]
      );
      
      if (offersToGeocode.length === 0) return;
      
      console.log(`🌍 Géocodage parallèle de ${offersToGeocode.length} adresses...`);
      
      // Géocoder en parallèle (plus rapide)
      const geocodePromises = offersToGeocode.map(async (o) => {
        try {
          // Utiliser les coordonnées GPS si disponibles (pour les parkings)
          if (o.latitude && o.longitude) {
            const geo = {
              lat: parseFloat(o.latitude),
              lon: parseFloat(o.longitude)
            };
            console.log('✅ Utilisation coordonnées GPS pour', o.driverName, ':', geo);
            return { id: o.id, geo };
          }
          
          // Sinon, utiliser l'adresse réelle si disponible (pour les parkings)
          const addressToGeocode = o.realAddress || o.startAddress || o.address || "";
          const addressString = `${addressToGeocode}, ${o.postalCode || ""} ${o.city || ""}`.trim();
          
          if (!addressString || addressString === ",") {
            console.warn('⚠️ Adresse vide pour conducteur:', o.driverName);
            return { id: o.id, geo: null };
          }
          
          console.log('🌍 Géocodage:', addressString);
          
          // Ajouter un timeout pour éviter les blocages
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout géocodage')), 3000)
          );
          
          const geoPromise = geocodeAddress(addressString);
          const geo = await Promise.race([geoPromise, timeoutPromise]);
          
          if (geo && geo.lat && geo.lon) {
            console.log('✅ Géocodage réussi pour', o.driverName);
            return { id: o.id, geo };
          } else {
            console.warn('⚠️ Coordonnées invalides pour:', o.driverName);
            return { id: o.id, geo: null };
          }
        } catch (e) {
          // Ne pas bloquer l'affichage si le géocodage échoue
          console.warn('⚠️ Erreur géocodage pour', o.driverName, ':', e.message);
          return { id: o.id, geo: null };
        }
      });
      
      // Attendre tous les géocodages en parallèle
      const results = await Promise.allSettled(geocodePromises);
      
      // Mettre à jour l'état avec tous les résultats
      const newOfferGeo = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.geo) {
          newOfferGeo[result.value.id] = result.value.geo;
        }
      });
      
      if (Object.keys(newOfferGeo).length > 0) {
        setOfferGeo((prev) => ({ ...prev, ...newOfferGeo }));
        console.log(`✅ ${Object.keys(newOfferGeo).length} géocodages mis à jour`);
      }
    })();
  }, [offers, offerGeo]);

  // Fonction pour déterminer le badge de proximité
  const getProximityBadge = (distance) => {
    if (distance === Infinity) return null;
    
    if (distance <= 5) {
      return { text: "Très proche", color: "#10b981", icon: "🟢" }; // Vert
    } else if (distance <= 15) {
      return { text: "Proche", color: "#f59e0b", icon: "🟡" }; // Orange
    } else if (distance <= 30) {
      return { text: "Moyen", color: "#3b82f6", icon: "🔵" }; // Bleu
    } else {
      return { text: "Loin", color: "#6b7280", icon: "⚪" }; // Gris
    }
  };

  const enriched = useMemo(() => {
    const req = currentRequest || request; // Utiliser la demande mise à jour en temps réel
    
    // Afficher les conducteurs même sans géolocalisation de la demande
    return offers
      .map((o) => {
        // Utiliser les coordonnées GPS du conducteur si disponibles (pour les parkings)
        let driverGeo = o.geo || offerGeo[o.id] || null;
        if (!driverGeo && o.latitude && o.longitude) {
          driverGeo = {
            lat: parseFloat(o.latitude),
            lon: parseFloat(o.longitude)
          };
          console.log('✅ Utilisation coordonnées GPS conducteur:', o.driverName, driverGeo);
        }
        
        let dist = Infinity;
        let distLabel = "Distance inconnue";
        let proximityBadge = null;
        
        // Calculer la distance seulement si on a les deux géolocalisations
        if (requestGeo && driverGeo) {
          try {
            dist = haversineKm(requestGeo, driverGeo);
            distLabel = formatKm(dist);
            proximityBadge = getProximityBadge(dist);
          } catch (error) {
            console.warn('⚠️ Erreur calcul distance pour', o.driverName, error);
            dist = Infinity;
            distLabel = "Distance inconnue";
          }
        } else if (!requestGeo && !driverGeo) {
          // Aucune géolocalisation disponible
          distLabel = "Distance non disponible";
        } else if (!requestGeo) {
          // Pas de géolocalisation de la demande
          distLabel = "En attente...";
        } else {
          // Pas de géolocalisation du conducteur (en cours de calcul)
          distLabel = "Calcul en cours...";
        }
        
        // Places prises pour CETTE demande (si le conducteur prend déjà ce passager)
        // Normaliser la clé pour accéder aux données (compatibilité avec les noms contenant des caractères spéciaux)
        const normalizeKey = (name) => String(name || '').replace(/[.#$/\[\]]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
        const driverKey = normalizeKey(o.driverName);
        const hasEntryForDriver = req.drivers && (req.drivers[driverKey] || req.drivers[o.driverName]); // Support ancien format
        const driverData = hasEntryForDriver ? (req.drivers[driverKey] || req.drivers[o.driverName]) : null;
        const assignedSeats = driverData ? (driverData.seatsTaken || 0) : 0;

        // Capacité globale restante de l'offre (toutes demandes confondues)
        const totalSeats = Number(o.seats || 1);
        const globalTaken = Number(o.seatsTaken || (typeof o.seatsAvailable === 'number' ? (totalSeats - o.seatsAvailable) : 0));
        const seatsAvailableGlobal = Math.max(0, typeof o.seatsAvailable === 'number' ? o.seatsAvailable : (totalSeats - globalTaken));

        // Capacité affichée pour cette carte: on s'appuie d'abord sur la capacité globale restante
        const seatsAvailable = seatsAvailableGlobal;
        const isAssigned = Boolean(hasEntryForDriver);
        
        return {
          ...o,
          _distanceKm: dist,
          _distanceLabel: distLabel,
          _seatsAvailable: seatsAvailable,
          _seatsAvailableGlobal: seatsAvailableGlobal,
          _proximityBadge: proximityBadge,
          _isAssigned: isAssigned,
          _assignedSeats: assignedSeats,
          _totalSeats: totalSeats,
        };
      })
      .sort((a, b) => {
        // Les conducteurs assignés en premier
        if (a._isAssigned && !b._isAssigned) return -1;
        if (!a._isAssigned && b._isAssigned) return 1;
        
        // Puis trier par distance pour les non-assignés
        if (!a._isAssigned && !b._isAssigned) {
          // Les distances en cours de calcul (Infinity) vont à la fin, mais on les affiche quand même
          if (a._distanceKm === Infinity && b._distanceKm === Infinity) {
            return b._seatsAvailable - a._seatsAvailable;
          }
          if (a._distanceKm === Infinity) return 1;
          if (b._distanceKm === Infinity) return -1;
          
          if (Math.abs(a._distanceKm - b._distanceKm) < 0.05) {
            return b._seatsAvailable - a._seatsAvailable;
          }
          return a._distanceKm - b._distanceKm;
        }
        
        // Pour les assignés, trier par nombre de places prises (décroissant)
        return (b._assignedSeats || 0) - (a._assignedSeats || 0);
      })
      // Filtrer pour afficher TOUS les conducteurs, même sans géolocalisation
      .filter(o => o); // Pas de filtre, on affiche tout
  }, [offers, requestGeo, offerGeo, currentRequest, request]);

  const handleTake = async (offer) => {
    const req = currentRequest || request; // Utiliser la demande mise à jour en temps réel

    // Calculer automatiquement le nombre de places à prendre
    const maxSeats = Math.min(offer._seatsAvailable, req.seatsRemaining || req.seatsNeeded || 1);
    const seats = maxSeats; // Prendre automatiquement toutes les places disponibles
    
    console.log(`🚗 [RequestNearbyDriversModal] Prise automatique de ${seats} place(s) sur ${req.seatsNeeded || 1} demandées`);

    // Exécuter directement sans confirmation
    const res = await takeSeatForRequest({
      requestId: req.id,
      driverOfferId: offer.id,
      driverName: offer.driverName,
      seatsToTake: seats,
    });
    if (res.ok) {
      alert(`Vous prenez en charge ${seats} place(s) pour ${req.name || req.passengerName}`);
      onClose && onClose(true);
    } else {
      alert(`Erreur : ${res.error || "Ce conducteur est complet"}`);
    }
  };

  const handleCancelOffer = (offer) => {
    setSelectedOffer(offer);
    setShowCancelModal(true);
  };

  const confirmCancelOffer = async (cancelAllDates = false) => {
    if (!selectedOffer) return;

    try {
      console.log("🗑️ Confirmation de suppression:", {
        driverName: selectedOffer.driverName,
        cancelAllDates,
        offerId: selectedOffer.id,
      });

      const result = await deleteDriverOffer({
        driverOfferId: selectedOffer.id,
        driverName: selectedOffer.driverName,
        cancelAllDates,
      });

      if (result.ok) {
        setShowCancelModal(false);
        setSelectedOffer(null);

        if (cancelAllDates) {
          alert(
            `Offre de ${selectedOffer.driverName} supprimée pour toutes les dates`,
          );
        } else {
          alert(
            `Offre de ${selectedOffer.driverName} supprimée pour cette date`,
          );
        }

        // Fermer la modale principale pour recharger les données
        onClose && onClose(false);
      } else {
        alert(
          `Erreur lors de la suppression: ${result.error || "Erreur inconnue"}`,
        );
      }
    } catch (error) {
      console.error("❌ Erreur lors de l'annulation:", error);
      alert("Erreur lors de l'annulation de l'offre");
    }
  };

  const buttonStyle = {
    background: "#4285f4",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s",
    textDecoration: "none",
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="modal-card"
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "16px 12px", // Réduit le padding horizontal
          width: "95%",
          maxWidth: 480, // Réduit la largeur maximale
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3>Conducteurs proches</h3>
          <button onClick={() => onClose && onClose(false)}>×</button>
        </div>
        {loading && <p>Chargement…</p>}
        {error && <p style={{ color: "#b00020" }}>{error}</p>}
        {!loading &&
          !error &&
          (enriched.length === 0 ? (
            <>
              <p>Aucune offre disponible pour cette date.</p>
              <p style={{ marginTop: 8 }}>
                Si votre nom n'apparaît pas, veuillez vous inscrire{" "}
                <button
                  type="button"
                  style={buttonStyle}
                  onClick={(e) => {
                    e.preventDefault();
                    onGoToDriverForm && onGoToDriverForm();
                    onClose && onClose(false);
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#3367d6")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#4285f4")
                  }
                >
                  ici
                </button>
                .
              </p>
            </>
          ) : (
            <div>
              {enriched.map((offer) => {
                const full = offer._seatsAvailable <= 0;
                return (
                  <div
                    key={offer.id}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                      opacity: full ? 0.6 : 1,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "6px", 
                          marginBottom: "4px",
                          minHeight: "20px" // Hauteur minimale pour éviter les décalages
                        }}>
                          <strong style={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0 // Permet au texte de se rétrécir
                          }}>
                            {offer.driverName}
                          </strong>
                          {offer._proximityBadge && (
                            <span
                              style={{
                                backgroundColor: offer._proximityBadge.color,
                                color: "white",
                                padding: "1px 6px",
                                borderRadius: "8px",
                                fontSize: "10px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                flexShrink: 0, // Empêche le badge de se rétrécir
                                minWidth: "fit-content", // Taille minimale basée sur le contenu
                                whiteSpace: "nowrap" // Empêche le texte du badge de se couper
                              }}
                            >
                              {offer._proximityBadge.icon} {offer._proximityBadge.text}
                            </span>
                          )}
                        </div>
                        <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                          <strong style={{ color: "#6b7280" }}>{offer._distanceLabel}</strong>
                          <span style={{ 
                            marginLeft: "8px", 
                            fontSize: "12px", 
                            color: "#9ca3af",
                            fontWeight: "normal"
                          }}>
                            • {(() => {
                              const totalSeats = offer._totalSeats || offer.seats || 1;
                              const seatsAvail = offer._seatsAvailableGlobal ?? offer._seatsAvailable ?? 0;
                              const seatsTakenGlobal = Math.max(0, totalSeats - seatsAvail);
                              return `${seatsTakenGlobal}/${totalSeats} places`;
                            })()}
                          </span>
                        </p>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                          flexShrink: 0, // Empêche la réduction de cette section
                          minWidth: 0, // Permet la réduction si nécessaire
                        }}
                      >
                        {/* Icône poubelle pour annuler l'offre */}
                        <button
                          type="button"
                          onClick={() => handleCancelOffer(offer)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#dc2626",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            fontSize: "16px",
                            flexShrink: 0, // Empêche la réduction de l'icône
                          }}
                          title="Annuler cette offre"
                        >
                          🗑️
                        </button>

                        {full ? (
                          <span
                            style={{
                              background: "#fdeaea",
                              color: "#b91c1c",
                              padding: "4px 8px",
                              borderRadius: 999,
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            Complet
                          </span>
                        ) : (
                          <button
                            className="accept-button"
                            onClick={() => handleTake(offer)}
                            style={{
                              fontSize: "12px",
                              padding: "6px 10px",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                              maxWidth: "100px",
                            }}
                          >
                            {offer._isAssigned ? "Prendre le reste" : "Je le prends"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <p style={{ marginTop: 8 }}>
                Votre nom n'apparaît pas ? Inscrivez votre voiture{" "}
                <button
                  type="button"
                  style={buttonStyle}
                  onClick={(e) => {
                    e.preventDefault();
                    onGoToDriverForm && onGoToDriverForm();
                    onClose && onClose(false);
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#3367d6")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#4285f4")
                  }
                >
                  ici
                </button>
                .
              </p>
            </div>
          ))}
      </div>

      {/* Modale de confirmation pour l'annulation */}
      {showCancelModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="modal-card"
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: "90%",
              maxWidth: 400,
            }}
          >
            <h4 style={{ margin: "0 0 16px 0", color: "#dc2626" }}>
              🗑️ Annuler l'offre de {selectedOffer?.driverName}
            </h4>

            <p style={{ margin: "0 0 20px 0", color: "#374151" }}>
              Voulez-vous annuler cette offre ?
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <button
                type="button"
                onClick={() => confirmCancelOffer(true)}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                🗓️ Annuler pour toutes les dates
              </button>

              <button
                type="button"
                onClick={() => confirmCancelOffer(false)}
                style={{
                  background: "#f59e0b",
                  color: "white",
                  border: "none",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                📅 Annuler seulement pour cette date
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedOffer(null);
                }}
                style={{
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RequestNearbyDriversModal;
